import { useEffect, useRef } from 'react'

import { envConfig } from '../lib/env-config'

interface INotification<T> {
  body?: string
  data?: T & {
    messageID: string
    silent?: boolean
    skipBrowserNotification?: boolean
  }
  notificationHandler?: () => void
  title: string
}

/**
 * Hook for handling browser notifications with multi-tab coordination
 * @returns Object with create function and other notification utilities
 */
export function useBrowserNotification() {
  // Refs to maintain state across renders
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const tabIDRef = useRef<string>('')
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Constants for leader election
  const LEADER_KEY = 'notification_leader_tab'
  const HEARTBEAT_INTERVAL = 5000

  /**
   * Configure HTML Audio element for optimal quality playback
   */
  const configureAudioElement = (audioElement: HTMLAudioElement): void => {
    // Preload the entire audio file to avoid delays and ensure smooth playback
    audioElement.preload = 'auto'

    // Set volume to maximum to prevent browser from applying auto-gain reduction
    audioElement.volume = 1.0

    // Extended audio element interface to access quality-related properties
    const extendedAudioElement = audioElement as HTMLAudioElement & {
      preferredQuality?: string
      preservesPitch?: boolean
    }

    // Prevent pitch changes during audio processing (reduces artifacts)
    if ('preservesPitch' in extendedAudioElement) {
      extendedAudioElement.preservesPitch = true
    }

    // Request high-quality audio decoding when supported
    if ('preferredQuality' in extendedAudioElement) {
      extendedAudioElement.preferredQuality = 'high'
    }
  }

  /**
   * Initialize Web Audio API context with high-quality settings
   */
  const initializeAudioContext = (): (() => void) | void => {
    try {
      // Configure AudioContext for optimal audio quality
      const audioContextOptions: AudioContextOptions = {
        // Prioritize audio quality over low latency for notification sounds
        latencyHint: 'playback',
        // Use 48kHz sample rate (higher than default 44.1kHz) for better quality
        sampleRate: 48000
      }

      // Create the AudioContext with quality-optimized settings
      audioContextRef.current = new AudioContext(audioContextOptions)

      // Handle browser tab visibility changes
      const handleVisibilityChange = () => {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended' && !document.hidden) {
          audioContextRef.current.resume()
        }
      }

      // Create function to resume AudioContext on user interaction
      const resumeAudioContext = () => {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume()
        }
      }

      // Listen for user interactions to resume audio if suspended
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('click', resumeAudioContext)
      document.addEventListener('keydown', resumeAudioContext)

      // Return cleanup function
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        document.removeEventListener('click', resumeAudioContext)
        document.removeEventListener('keydown', resumeAudioContext)
      }
    } catch (error) {
      // If AudioContext creation fails, log warning and continue without it
      console.warn('AudioContext initialization failed:', error)
      audioContextRef.current = null
    }
  }

  /**
   * Check if this tab is currently the notification leader
   */
  const isLeaderTab = (): boolean => {
    const currentLeader = localStorage.getItem(LEADER_KEY)

    // If no leader is stored, we're not the leader
    if (!currentLeader) return false

    // Extract the leader tab ID and compare with ours
    const [leaderID] = currentLeader.split(':')
    return leaderID === tabIDRef.current
  }

  /**
   * Attempt to become or maintain leadership for notifications
   */
  const attemptLeadership = (): void => {
    const currentLeader = localStorage.getItem(LEADER_KEY)
    const now = Date.now()

    if (!currentLeader) {
      // No current leader exists, so we become the leader
      localStorage.setItem(LEADER_KEY, `${tabIDRef.current}:${now}`)
    } else {
      // Parse the leader data: "tabID:timestamp"
      const [leaderID, lastHeartbeat] = currentLeader.split(':')

      // Check if the current leader is stale (hasn't sent heartbeat recently)
      if (now - parseInt(lastHeartbeat) > HEARTBEAT_INTERVAL * 2) {
        // Leader is stale, take over leadership
        localStorage.setItem(LEADER_KEY, `${tabIDRef.current}:${now}`)
      } else if (leaderID === tabIDRef.current) {
        // We're already the leader, just update our heartbeat timestamp
        localStorage.setItem(LEADER_KEY, `${tabIDRef.current}:${now}`)
      }
    }
  }

  /**
   * Start the leader election process for cross-tab notification coordination
   */
  const startLeaderElection = (): (() => void) => {
    // Immediately attempt to become leader
    attemptLeadership()

    // Set up periodic heartbeat to maintain leadership and detect stale leaders
    heartbeatIntervalRef.current = setInterval(() => {
      attemptLeadership()
    }, HEARTBEAT_INTERVAL)

    // Clean up leadership when this tab is closing
    const handleBeforeUnload = () => {
      // Stop the heartbeat timer
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }

      // If we're the current leader, remove leadership
      const currentLeader = localStorage.getItem(LEADER_KEY)
      if (currentLeader === tabIDRef.current) {
        localStorage.removeItem(LEADER_KEY)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Return cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current)
      }
    }
  }

  /**
   * Primary playback method using pure HTML Audio element
   */
  const playWithHTMLAudio = async (): Promise<void> => {
    if (!notificationSoundRef.current) return

    // Reset playback position to the beginning of the audio file
    notificationSoundRef.current.currentTime = 0

    // Initiate pure HTML Audio playback
    const playPromise = notificationSoundRef.current.play()

    // Handle both Promise-based and legacy play() implementations
    if (playPromise !== undefined) {
      await playPromise
    }
  }

  /**
   * Fallback playback method with AudioContext state management
   */
  const playWithAudioContext = async (): Promise<void> => {
    if (!notificationSoundRef.current) return

    // Ensure AudioContext is active (not suspended by browser policies)
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      // Resume the AudioContext to enable audio playback
      await audioContextRef.current.resume()
    }

    // Reset audio playback position to start from the beginning
    notificationSoundRef.current.currentTime = 0

    // Attempt to play the audio with AudioContext state management
    const playPromise = notificationSoundRef.current.play()

    // Wait for playback to start successfully
    if (playPromise !== undefined) {
      await playPromise
    }
  }

  /**
   * Play notification sound with multi-method approach and error handling
   */
  const playNotificationSound = async (): Promise<void> => {
    try {
      // Method 1: Pure HTML Audio - often produces the highest quality
      await playWithHTMLAudio()
    } catch (error) {
      console.warn('HTML Audio playback failed, trying with AudioContext:', error)

      try {
        // Method 2: AudioContext-managed playback as fallback
        await playWithAudioContext()
      } catch (fallbackError) {
        console.warn('All playback methods failed:', fallbackError)
      }
    }
  }

  /**
   * Debug method to check notification leadership status
   */
  const debugLeadershipStatus = () => {
    const currentLeader = localStorage.getItem(LEADER_KEY)

    return {
      currentLeader,
      isLeader: isLeaderTab(),
      myTabID: tabIDRef.current
    }
  }

  /**
   * Debug method to test sound playback
   */
  const debugSoundPlayback = async () => {
    if (isLeaderTab()) {
      await playNotificationSound()
    }
  }

  /**
   * Creates and displays a browser notification
   */
  const create = <T>(notification: INotification<T>): void => {
    const { title, body, data, notificationHandler } = notification

    // Early exit: Skip notification if explicitly marked to be skipped or silent
    if (data?.skipBrowserNotification || data?.silent) {
      return
    }

    // Early exit: Only the leader tab should handle notifications
    if (!isLeaderTab()) {
      return
    }

    // Verify browser support and user permission for notifications
    if ('Notification' in window && window.Notification && Notification.permission === 'granted') {
      // Create the browser notification with optimized settings
      const newNotification = new Notification(title, {
        body: body || '',
        data: data,
        icon: envConfig.browserNotificationIcon || '',
        tag: data?.messageID || ''
      })

      // Set up click handler if provided
      if (notificationHandler) {
        newNotification.onclick = () => {
          window.focus()
          notificationHandler()
          newNotification.close()
        }
      }

      // Play the notification sound using our high-quality audio system
      playNotificationSound()
    } else {
      console.warn('Browser notifications not supported or permission not granted')
    }
  }

  /**
   * Requests permission for browser notifications
   */
  const requestPermission = async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission !== 'denied') {
      return await Notification.requestPermission()
    }

    return Notification.permission
  }

  /**
   * Checks if browser notifications are supported and permission is granted
   */
  const isNotificationAvailable = (): boolean => {
    return 'Notification' in window && Notification.permission === 'granted'
  }

  // Initialize everything when the hook mounts
  useEffect(() => {
    // Generate unique ID for this tab
    tabIDRef.current = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

    // Initialize audio element
    notificationSoundRef.current = new Audio(envConfig.browserNotificationSound)
    configureAudioElement(notificationSoundRef.current)

    // Initialize audio context
    const audioContextCleanup = initializeAudioContext()

    // Start leader election
    const leaderElectionCleanup = startLeaderElection()

    // Cleanup function
    return () => {
      // Clean up audio context
      if (audioContextCleanup) {
        audioContextCleanup()
      }

      // Clean up leader election
      if (leaderElectionCleanup) {
        leaderElectionCleanup()
      }

      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, []) // Empty dependency array since we don't need to re-run this effect

  return {
    create,
    currentPermission: typeof Notification !== 'undefined' ? Notification.permission : 'default',
    debugLeadershipStatus,
    debugSoundPlayback,
    isLeaderTab,
    isNotificationAvailable,
    requestPermission
  }
}
